import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  useTheme,
  List,
  ListItem,
  Divider,
  Button,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Grid,
  Paper,
  Stack,
  Alert,
  Snackbar,
  Tooltip as MuiTooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ArticleIcon from '@mui/icons-material/Article';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SettingsIcon from '@mui/icons-material/Settings';
import CodeIcon from '@mui/icons-material/Code';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import DownloadIcon from '@mui/icons-material/Download';
import { tokens, fonts } from '../theme';
import { useAppContext } from '../contexts/AppContext';
import { authenticatedFetch } from '../utils/api';
import { API_BASE } from '../config/environment';
import { resolveDatasetDisplayNameForExecution } from '../utils/datasetDisplay';
import { useNavigate } from 'react-router-dom';
import { STATUS_COLORS } from '../constants';
import ExecutionLogsCard from '../scenes/execute/components/ExecutionLogsCard';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const AllCompletedRunsModal = ({ open, onClose, activities, onActivityClick, initialSelectedRunId, initialSelectedActivity }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const {
    fetchTrainingSummary,
    saveConfig,
    setCurrentConfig,
    fetchTrainingMetrics,
    // Needed by the Training Runs list row subtext so dataset display matches
    // the Configuration Details Dataset row resolver (sites[] → siteCapabilities
    // → dataset_ref → mockDatasetDefinitions.display_name). Without these the
    // resolver falls back to file-tail on legacy capability_id strings.
    siteCapabilities,
    availableDatasets,
    // dev-0.7.50: Execution Details Configuration tab resolves each
    // config_data.sites[].site_id back to a friendly site display name
    // (e.g. "Toronto Grove Hospital") via the AppContext sites list.
    sites,
  } = useAppContext();
  
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'completed', 'incomplete'
  const [selectedRun, setSelectedRun] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0: Details, 1: Execution, 2: Logs
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [executionLogs, setExecutionLogs] = useState([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  // dev-0.7.46 — Execution Details "Execution" tab embeds the Tier1
  // result card HTML (when available) instead of the legacy loss/accuracy
  // charts. State machine:
  //   'idle'    — no flower_task_id on this run (pre-Tier1 historic row)
  //               → render legacy charts as fallback
  //   'loading' — fetch in flight
  //   'ok'      — blob URL ready → render iframe
  //   'error'   — fetch failed (e.g. card not uploaded, 404, network)
  //               → render legacy charts as fallback
  // Uses authenticatedFetch + blob URL because iframes cannot send Bearer
  // headers and the Tier1 endpoint requires auth (same workaround as
  // PipelineResultCardViewer).
  const [cardBlobUrl, setCardBlobUrl] = useState(null);
  const [cardState, setCardState] = useState('idle');
  // Tracks the flower task_id once we successfully resolve it for the
  // selected run, so the download-icon button on the iframe can build a
  // stable filename like federatex_<taskId-prefix>_Tier1_result_card.html
  // (same convention as PipelineResultCardViewer).
  const [cardTaskId, setCardTaskId] = useState(null);

  // dev-0.7.46 — Fetch + cache Tier1 result card blob for Execution tab.
  // Re-fires whenever the user opens a different run or switches to the
  // Execution tab. Cleans up the blob URL on unmount or selection change
  // to avoid leaks.
  useEffect(() => {
    if (!selectedRun || activeTab !== 1) {
      // Different tab or no run — release any cached blob URL.
      setCardBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setCardState('idle');
      setCardTaskId(null);
      return undefined;
    }
    // Resolve task_id through 3 sources (in priority order). The training-
    // summary list view doesn't include flower_task_id today, so most rows
    // need the detail-endpoint fallback. Same pattern dashboard uses for
    // "Use Configuration".
    //
    //   Source 1: row-level flower_task_id (if backend later extends the
    //             /api/user/training-summary SELECT)
    //   Source 2: row-level config_data.flower_task_id (if backend includes
    //             config_data in training-summary)
    //   Source 3: per-row detail fetch /api/user/training-runs/<id> →
    //             whatever shape that returns
    let cancelled = false;
    let createdBlobUrl = null;
    setCardState('loading');
    (async () => {
      let taskId =
        selectedRun.executionData?.flower_task_id ||
        selectedRun.executionData?.config_data?.flower_task_id ||
        null;

      // Source 3 — detail endpoint
      if (!taskId) {
        const trainingRunId = selectedRun.executionData?.trainingRunId;
        if (trainingRunId) {
          try {
            const detailRes = await authenticatedFetch(
              `${API_BASE}/user/training-runs/${trainingRunId}`
            );
            if (detailRes && detailRes.ok) {
              const detailData = await detailRes.json();
              const detailRun = detailData?.training_run || detailData || {};
              taskId =
                detailRun?.flower_task_id ||
                detailRun?.config_data?.flower_task_id ||
                detailRun?.task_id ||
                null;
            }
          } catch (err) {
            // Detail-endpoint miss is non-fatal — falls through to legacy widget.
          }
        }
      }

      if (cancelled) return;

      if (!taskId) {
        // All 3 sources exhausted → backend really doesn't have this for
        // the current row. Fall back to legacy widget.
        setCardState('idle');
        return;
      }

      try {
        const res = await authenticatedFetch(
          `${API_BASE}/flower_tasks/${taskId}/tier1/Tier1_result_card.html`
        );
        if (!res || !res.ok) {
          throw new Error(`Tier1 card fetch HTTP ${res?.status || 'unknown'}`);
        }
        const blob = await res.blob();
        if (cancelled) return;
        if (!blob || blob.size === 0) {
          throw new Error('Tier1 card blob empty');
        }
        createdBlobUrl = URL.createObjectURL(blob);
        setCardBlobUrl(createdBlobUrl);
        setCardTaskId(taskId);
        setCardState('ok');
      } catch (err) {
        if (cancelled) return;
        console.warn('[AllCompletedRunsModal] Tier1 card fetch failed:', err?.message || err);
        setCardState('error');
      }
    })();
    return () => {
      cancelled = true;
      if (createdBlobUrl) URL.revokeObjectURL(createdBlobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRun, activeTab]);

  // Fetch logs when tab changes to Logs (index 2)
  useEffect(() => {
    if (selectedRun && activeTab === 2) {
      const loadLogs = async () => {
        setIsLoadingLogs(true);
        try {
          // Access trainingRunId from executionData (where our mapper puts it) or directly from id if structure differs
          const runId = selectedRun.executionData?.trainingRunId || selectedRun.id;
          console.log('[AllCompletedRunsModal] Fetching logs for run:', runId);
          if (runId) {
             const metrics = await fetchTrainingMetrics(runId);
             console.log('[AllCompletedRunsModal] Fetched metrics:', metrics ? 'found' : 'null');
             if (metrics && Array.isArray(metrics)) {
               // Find any metric entry that has execution_logs
               // Since we append full logs to the final round, any non-empty logs entry should be what we want.
               // We reverse sort by round/id to likely get the latest.
               const sortedMetrics = [...metrics].sort((a, b) => b.id - a.id);
               const logsEntry = sortedMetrics.find(m => m.execution_logs && m.execution_logs.length > 0);
               
               if (logsEntry) {
                 // Parse if string, otherwise use as is
                 let parsedLogs = logsEntry.execution_logs;
                 if (typeof parsedLogs === 'string') {
                    try { parsedLogs = JSON.parse(parsedLogs); } catch(e) {}
                 }
                 setExecutionLogs(parsedLogs || []);
               } else {
                 setExecutionLogs([]);
               }
             }
          }
        } catch (error) {
          console.error("[AllCompletedRunsModal] Failed to fetch logs:", error);
        } finally {
          setIsLoadingLogs(false);
        }
      };
      
      loadLogs();
    }
  }, [selectedRun, activeTab, fetchTrainingMetrics]);

  // Constants
  const TABS = [
    { label: 'Details', icon: <AssessmentIcon /> },
    { label: 'Execution', icon: <TrendingUpIcon /> },
    { label: 'Logs', icon: <CodeIcon /> }
  ];

  // Initialize selected run from props or reset
  useEffect(() => {
    if (open) {
      // Prefer the full activity object the caller passed (works for mock data
      // and any run without a trainingRunId). Fall back to lookup by ID against
      // the activities list, which still supports deep-link style flows.
      if (initialSelectedActivity) {
        setSelectedRun(initialSelectedActivity);
        setActiveTab(0);
      } else if (initialSelectedRunId) {
        const foundRun = activities.find(a => a.executionData?.trainingRunId === initialSelectedRunId || a.id === initialSelectedRunId);
        if (foundRun) {
          setSelectedRun(foundRun);
          setActiveTab(0);
        }
      }
      // Do not reset selectedRun to null here; it kills manual navigation on data refresh
    } else {
      setSelectedRun(null);
    }
  }, [open, initialSelectedRunId, initialSelectedActivity, activities]);

  // Derived state: Filtered Activities
  const filteredActivities = useMemo(() => {
    return activities.filter(activity => {
      // 1. Text Search
      const searchLower = searchQuery.toLowerCase();
      const modelName = activity.executionData?.modelType || activity.title || '';
      const datasetName = activity.executionData?.datasetPath || activity.description || '';
      const matchesSearch = modelName.toLowerCase().includes(searchLower) || datasetName.toLowerCase().includes(searchLower);

      // 2. Status Filter
      let matchesFilter = true;
      const isCompleted = activity.status === 'completed' || activity.executionData?.completed;
      
      if (filterStatus === 'completed') matchesFilter = isCompleted;
      if (filterStatus === 'incomplete') matchesFilter = !isCompleted;

      return matchesSearch && matchesFilter;
    });
  }, [activities, searchQuery, filterStatus]);

  // Handlers
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleUseConfiguration = async (run) => {
    setIsLoadingConfig(true);
    // Reuse the existing logic passed from parent or reimplement here if needed
    // For now assuming onActivityClick does the config loading
    try {
        await onActivityClick(run);
        setSnackbar({ open: true, message: 'Configuration loaded successfully!', severity: 'success' });
    } catch (error) {
        setSnackbar({ open: true, message: 'Failed to load configuration.', severity: 'error' });
    } finally {
        setIsLoadingConfig(false);
    }
  };

  const getStatusColor = (status, isCompleted) => {
    if (isCompleted || status === 'completed') return STATUS_COLORS.COMPLETED;
    if (status === 'running') return colors.blueAccent?.[500] || '#ffa726';
    if (status === 'paused') return '#ffa726'; // Orange for paused
    if (status === 'failed') return colors.redAccent?.[500] || '#db4f4a';
    if (status === 'incomplete') return STATUS_COLORS.INCOMPLETE;
    return colors.grey[500];
  };

  const getStatusIcon = (status, isCompleted) => {
      if (isCompleted || status === 'completed') return <CheckCircleIcon fontSize="small" />;
      if (status === 'running') return <HourglassEmptyIcon fontSize="small" />;
      if (status === 'paused') return <HourglassEmptyIcon fontSize="small" />;
      if (status === 'failed') return <ErrorIcon fontSize="small" />;
      if (status === 'incomplete') return <HourglassEmptyIcon fontSize="small" />;
      return <HourglassEmptyIcon fontSize="small" />;
  };

  // Render Content
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          height: '80vh',
          backgroundColor: '#ffffff',
          overflow: 'hidden'
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${colors.grey[700]}`,
          px: 3,
          py: 2,
          bgcolor: '#fafafa'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {selectedRun ? (
            <IconButton onClick={() => { setSelectedRun(null); setActiveTab(0); }} size="small">
              <ArrowBackIcon />
            </IconButton>
          ) : (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                backgroundColor: colors.primary[500],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff'
              }}
            >
               <AssessmentIcon />
            </Box>
          )}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: colors.grey[100] }}>
              {selectedRun ? 'Execution Details' : 'Training Runs'}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.grey[600] }}>
              {selectedRun 
                ? `ID: ${selectedRun.executionData?.trainingRunId || 'N/A'}` 
                : `${filteredActivities.length} runs found`}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
           <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#ffffff' }}>
        
        {/* VIEW 1: LIST OF RUNS */}
        {!selectedRun && (
          <>
            {/* Toolbar */}
            <Box sx={{ p: 2, borderBottom: `1px solid ${colors.grey[700]}`, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
               <TextField
                placeholder="Search by model or dataset..."
                variant="outlined"
                size="small"
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: colors.grey[400] }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: 1, minWidth: '200px' }}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                {['all', 'completed', 'incomplete'].map((status) => (
                    <Chip 
                        key={status}
                        label={status.charAt(0).toUpperCase() + status.slice(1)}
                        onClick={() => setFilterStatus(status)}
                        color={filterStatus === status ? "primary" : "default"}
                        variant={filterStatus === status ? "filled" : "outlined"}
                        sx={{ textTransform: 'capitalize' }}
                    />
                ))}
              </Box>
            </Box>

            {/* List */}
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
                {filteredActivities.length > 0 ? (
                    <List>
                        {filteredActivities.map((run, index) => {
                            const isCompleted = run.status === 'completed' || run.executionData?.completed;
                            const statusColor = getStatusColor(run.status, isCompleted);
                            
                            return (
                                <React.Fragment key={run.id || index}>
                                    <ListItem 
                                        sx={{ 
                                            flexDirection: 'column', 
                                            alignItems: 'stretch',
                                            cursor: 'pointer',
                                            transition: 'background-color 0.2s',
                                            '&:hover': { bgcolor: `${colors.primary[500]}15` } 
                                        }}
                                        onClick={() => setSelectedRun(run)}
                                    >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', p: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                 <Box sx={{ 
                                                     width: 40, 
                                                     height: 40, 
                                                     borderRadius: 1, 
                                                     bgcolor: statusColor,
                                                     display: 'flex',
                                                     alignItems: 'center',
                                                     justifyContent: 'center',
                                                     color: '#ffffff'
                                                 }}>
                                                     {getStatusIcon(run.status, isCompleted)}
                                                 </Box>
                                                 <Box>
                                                     <Typography variant="subtitle1" fontWeight="700" color={colors.primary[500]}>
                                                         {run.executionData?.modelType || 'Unknown Model'}
                                                     </Typography>
                                                     <Typography variant="body2" color={colors.grey[600]} fontWeight="500">
                                                         {run.time} • {resolveDatasetDisplayNameForExecution(
                                                             run.executionData,
                                                             { siteCapabilities, availableDatasets }
                                                         ) || 'Unknown Dataset'}
                                                     </Typography>
                                                 </Box>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button 
                                                    size="small" 
                                                    variant="outlined"
                                                    onClick={(e) => { 
                                                        e.stopPropagation();
                                                        setSelectedRun(run); 
                                                    }}
                                                    sx={{ 
                                                        color: colors.primary[500], 
                                                        borderColor: colors.primary[500],
                                                        minWidth: 'auto', 
                                                        p: '2px 10px', 
                                                        fontSize: '0.75rem',
                                                        fontWeight: 600,
                                                        '&:hover': {
                                                            backgroundColor: `${colors.primary[500]}10`,
                                                            borderColor: colors.primary[600],
                                                        }
                                                    }}
                                                >
                                                    View Details
                                                </Button>
                                            </Box>
                                        </Box>
                                    </ListItem>
                                </React.Fragment>
                            );
                        })}
                    </List>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', opacity: 0.5 }}>
                        <FilterListIcon sx={{ fontSize: 60, mb: 2 }} />
                        <Typography>No runs match your filters.</Typography>
                    </Box>
                )}
            </Box>
          </>
        )}

        {/* VIEW 2: RUN DETAILS */}
        {selectedRun && (
          <>
             <Tabs 
                value={activeTab} 
                onChange={handleTabChange} 
                sx={{ borderBottom: 1, borderColor: 'divider', px: 3, bgcolor: '#fafafa' }}
             >
                {TABS.map((tab, idx) => (
                    <Tab key={idx} icon={tab.icon} iconPosition="start" label={tab.label} />
                ))}
             </Tabs>

             <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
                {activeTab === 0 && (
                    <Grid container spacing={3}>
                        {/* ─── Header + Run Configuration + Hyperparameters (left col) ─── */}
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2, height: '100%' }} variant="outlined">
                                {(() => {
                                  const ed = selectedRun.executionData || {};
                                  const cd = ed.config_data || {};
                                  const dc = cd.dataset_config || {};

                                  // ── Pipeline category ──
                                  const pt = ed.processing_type || dc.processing_type || cd.processing_type;
                                  const PIPELINE_LABEL = {
                                    tabular: 'Tabular',
                                    imaging_classification: 'Imaging Classification',
                                    imaging_segmentation: 'Imaging Segmentation',
                                  };
                                  const pipelineLabel = PIPELINE_LABEL[pt] || null;

                                  // ── Dataset display name (same resolver list rows use) ──
                                  const datasetName = resolveDatasetDisplayNameForExecution(
                                    ed,
                                    { siteCapabilities, availableDatasets }
                                  );

                                  // ── Sites: config_data.sites[].site_id → AppContext sites display_name ──
                                  const savedSites = Array.isArray(cd.sites) ? cd.sites : [];
                                  const siteDisplayNames = savedSites.map((s) => {
                                    const match = (sites || []).find(
                                      (x) => x?.site_id === s?.site_id || x?.id === s?.site_id
                                    );
                                    return match?.display_name || match?.name || s?.site_id || '—';
                                  });

                                  // ── Status badge ──
                                  const status = ed.status || (ed.completed ? 'completed' : 'incomplete');
                                  const STATUS_COLOR = {
                                    completed: '#16a34a',
                                    failed: '#dc2626',
                                    running: '#0ea5e9',
                                    paused: '#d97706',
                                    incomplete: '#64748b',
                                  };
                                  const statusColor = STATUS_COLOR[status] || '#64748b';

                                  // ── Timing ──
                                  const fmtDuration = (sec) => {
                                    if (sec == null || isNaN(Number(sec))) return '—';
                                    const s = Math.round(Number(sec));
                                    if (s <= 0) return '—';
                                    const h = Math.floor(s / 3600);
                                    const m = Math.floor((s % 3600) / 60);
                                    const r = s % 60;
                                    if (h > 0) return `${h}h ${m}m`;
                                    if (m > 0) return `${m}m ${r}s`;
                                    return `${r}s`;
                                  };
                                  const fmtTimestamp = (iso) => {
                                    if (!iso) return '—';
                                    try {
                                      const d = new Date(iso);
                                      if (isNaN(d.getTime())) return '—';
                                      return d.toLocaleString();
                                    } catch (_) {
                                      return '—';
                                    }
                                  };
                                  const startISO = ed.timestamp;
                                  const durationSec = ed.duration;
                                  // Compute Ended client-side from start + duration when end_time isn't on the row
                                  const endISO = ed.end_time
                                    || (startISO && durationSec
                                        ? new Date(new Date(startISO).getTime() + Number(durationSec) * 1000).toISOString()
                                        : null);
                                  const durationText = fmtDuration(durationSec);

                                  // ── Hyperparameters ──
                                  const totalRounds = ed.total_rounds || ed.rounds || ed.num_server_rounds;
                                  const roundsCompleted = ed.total_rounds_completed;
                                  const roundsText = totalRounds != null
                                    ? (roundsCompleted != null ? `${totalRounds} (${roundsCompleted} completed)` : String(totalRounds))
                                    : null;

                                  // ── Federation ──
                                  const numClients = cd.num_clients;
                                  const federationType = cd.federation_type;
                                  const federationLabel = federationType === 'flower-cli-superlink'
                                    ? `Flower CLI${numClients ? ` (${numClients} clients)` : ''}`
                                    : (federationType || null);

                                  // ── Helper for a single row ──
                                  const Row = ({ label, value, mono = false }) => (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1.5, py: 0.55, borderBottom: '1px dashed #eef2f6' }}>
                                      <Typography sx={{ color: colors.grey[600], fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{label}</Typography>
                                      <Typography sx={{ color: colors.grey[100] || '#0f172a', fontSize: '0.8125rem', fontWeight: 600, textAlign: 'right', wordBreak: 'break-word', fontFamily: mono ? 'var(--fx-font-mono)' : 'inherit' }}>{value ?? '—'}</Typography>
                                    </Box>
                                  );

                                  return (
                                    <>
                                      {/* Header */}
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.5 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                          Training Run #{ed.trainingRunId || selectedRun.id || '—'}
                                        </Typography>
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: 999, bgcolor: `${statusColor}1a`, color: statusColor, fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                                          {status}
                                        </Box>
                                      </Box>
                                      <Typography sx={{ color: colors.grey[600], fontSize: '0.8125rem', fontWeight: 500, mb: 1.5 }}>
                                        {datasetName}{pipelineLabel ? ` · ${pipelineLabel}` : ''}
                                      </Typography>

                                      {/* Timing */}
                                      <Typography sx={{ color: colors.grey[600], fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', mt: 0.5, mb: 0.5 }}>Timing</Typography>
                                      <Row label="Started" value={fmtTimestamp(startISO)} mono />
                                      <Row label="Ended" value={fmtTimestamp(endISO)} mono />
                                      <Row label="Duration" value={durationText} mono />

                                      {/* Run Configuration */}
                                      <Typography sx={{ color: colors.grey[600], fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', mt: 1.5, mb: 0.5 }}>Run Configuration</Typography>
                                      <Row label="Dataset" value={datasetName} />
                                      {siteDisplayNames.length > 0 ? (
                                        <Box sx={{ py: 0.55, borderBottom: '1px dashed #eef2f6' }}>
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.4 }}>
                                            <Typography sx={{ color: colors.grey[600], fontSize: '0.75rem', fontWeight: 600 }}>Sites</Typography>
                                            <Typography sx={{ color: colors.grey[100] || '#0f172a', fontSize: '0.8125rem', fontWeight: 600 }}>
                                              {siteDisplayNames.length} {siteDisplayNames.length === 1 ? 'site' : 'sites'}
                                            </Typography>
                                          </Box>
                                          <Box sx={{ pl: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                            {siteDisplayNames.map((name, i) => (
                                              <Box key={`${name}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#0087B4' }} />
                                                <Typography sx={{ color: colors.grey[100] || '#0f172a', fontSize: '0.75rem' }}>{name}</Typography>
                                              </Box>
                                            ))}
                                          </Box>
                                        </Box>
                                      ) : (
                                        <Row label="Sites" value="—" />
                                      )}
                                      {pipelineLabel ? <Row label="Pipeline" value={pipelineLabel} /> : null}
                                      <Row label="Model" value={ed.modelType} />
                                      {federationLabel ? <Row label="Federation" value={federationLabel} /> : null}

                                      {/* Hyperparameters */}
                                      <Typography sx={{ color: colors.grey[600], fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', mt: 1.5, mb: 0.5 }}>Hyperparameters</Typography>
                                      {roundsText ? <Row label="Global rounds" value={roundsText} mono /> : null}
                                      {ed.local_epochs != null ? <Row label="Local epochs" value={ed.local_epochs} mono /> : null}
                                      {ed.batch_size != null ? <Row label="Batch size" value={ed.batch_size} mono /> : null}
                                      {ed.learning_rate != null ? <Row label="Learning rate" value={ed.learning_rate} mono /> : null}
                                      {ed.fraction_evaluate != null ? <Row label="Eval fraction" value={ed.fraction_evaluate} mono /> : null}
                                      {ed.partitioning_method ? <Row label="Partitioning" value={ed.partitioning_method} /> : null}
                                    </>
                                  );
                                })()}
                            </Paper>
                        </Grid>

                        {/* ─── Final Metrics (pipeline-aware) + conditional Failure (right col) ─── */}
                        <Grid item xs={12} md={6}>
                            <Stack spacing={2} sx={{ height: '100%' }}>
                                <Paper sx={{ p: 2 }} variant="outlined">
                                    {(() => {
                                      const ed = selectedRun.executionData || {};
                                      const dc = ed.config_data?.dataset_config || {};
                                      const pt = ed.processing_type || dc.processing_type;
                                      const isSeg = pt === 'imaging_segmentation';

                                      const finalAccuracy = ed.accuracy ?? ed.final_accuracy;
                                      const finalLoss = ed.loss ?? ed.final_loss;
                                      const finalDice = ed.final_dice_hard ?? ed.final_dice ?? ed.dice_hard;
                                      const bestAccuracy = ed.best_accuracy;
                                      const bestLoss = ed.best_loss;

                                      const fmtPct = (v) => (v == null || isNaN(Number(v))) ? '—' : `${(Number(v) * 100).toFixed(2)}%`;
                                      const fmtNum = (v) => (v == null || isNaN(Number(v))) ? '—' : Number(v).toFixed(4);

                                      const MetricBlock = ({ label, value, color }) => (
                                        <Box sx={{ flex: '1 1 45%', minWidth: 0 }}>
                                          <Typography sx={{ color: colors.grey[600], fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{label}</Typography>
                                          <Typography sx={{ color, fontSize: '1.5rem', fontWeight: 700, fontFamily: 'var(--fx-font-mono)', fontVariantNumeric: 'tabular-nums', mt: 0.25 }}>{value}</Typography>
                                        </Box>
                                      );

                                      return (
                                        <>
                                          <Typography variant="h6" gutterBottom>Final Metrics</Typography>
                                          <Divider sx={{ mb: 2 }} />
                                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                            {isSeg ? (
                                              <>
                                                <MetricBlock label="Final Dice (hard)" value={(finalDice == null || isNaN(Number(finalDice))) ? '—' : Number(finalDice).toFixed(4)} color={colors.greenAccent?.[500] || '#16a34a'} />
                                                <MetricBlock label="Final loss" value={fmtNum(finalLoss)} color={colors.redAccent?.[500] || '#dc2626'} />
                                              </>
                                            ) : (
                                              <>
                                                <MetricBlock label="Final accuracy" value={fmtPct(finalAccuracy)} color={colors.greenAccent?.[500] || '#16a34a'} />
                                                <MetricBlock label="Final loss" value={fmtNum(finalLoss)} color={colors.redAccent?.[500] || '#dc2626'} />
                                                {bestAccuracy != null ? <MetricBlock label="Best accuracy" value={fmtPct(bestAccuracy)} color="#0ea5e9" /> : null}
                                                {bestLoss != null ? <MetricBlock label="Best loss" value={fmtNum(bestLoss)} color="#7c5cbf" /> : null}
                                              </>
                                            )}
                                          </Box>
                                        </>
                                      );
                                    })()}
                                </Paper>

                                {/* Conditional Failure section */}
                                {(() => {
                                  const ed = selectedRun.executionData || {};
                                  const status = ed.status;
                                  if (status !== 'failed') return null;
                                  const msg = ed.error_message || 'Run failed. No error message captured.';
                                  return (
                                    <Paper sx={{ p: 2, borderColor: '#dc2626' }} variant="outlined">
                                      <Typography variant="h6" gutterBottom sx={{ color: '#dc2626' }}>Failure</Typography>
                                      <Divider sx={{ mb: 1.5 }} />
                                      <Typography sx={{ color: colors.grey[100] || '#0f172a', fontSize: '0.8125rem', fontFamily: 'var(--fx-font-mono)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {msg}
                                      </Typography>
                                    </Paper>
                                  );
                                })()}
                            </Stack>
                        </Grid>
                        {/* ─── Per-site contribution (real data only) ───────── */}
                        {(() => {
                          const ed = selectedRun.executionData || {};
                          const sitesPayload = ed.site_contributions || ed.per_site_rounds;
                          if (!Array.isArray(sitesPayload) || sitesPayload.length === 0) return null;
                          const sites = sitesPayload.map((s) => ({
                            name: s.display_name || s.name || s.site_id || 'Site',
                            rounds: Number(s.rounds_completed || s.rounds || 0),
                          })).sort((a, b) => b.rounds - a.rounds);
                          const palette = ['#0087B4', '#16a34a', '#7c5cbf', '#d97706', '#0f766e', '#4338ca'];
                          const grandTotal = sites.reduce((acc, s) => acc + s.rounds, 0) || 1;
                          return (
                            <Grid item xs={12} md={6}>
                              <Paper sx={{ p: 2, height: '100%' }} variant="outlined">
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
                                  <Typography variant="h6">Per-site contribution</Typography>
                                  <Typography variant="caption" sx={{ color: colors.grey[600], fontWeight: 600 }}>Rounds completed</Typography>
                                </Box>
                                <Divider sx={{ mb: 1.5 }} />
                                <Stack spacing={1.25}>
                                  {sites.map((s, i) => {
                                    const pct = Math.round((s.rounds / grandTotal) * 100);
                                    const c = palette[i % palette.length];
                                    return (
                                      <Box key={s.name + i}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.4 }}>
                                          <Typography sx={{ color: colors.grey[100] || '#0f172a', fontSize: '0.8rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</Typography>
                                          <Typography sx={{ color: c, fontWeight: 700, fontSize: '0.78rem', fontFamily: 'var(--fx-font-mono)', fontVariantNumeric: 'tabular-nums', flexShrink: 0, ml: 1 }}>{s.rounds}r · {pct}%</Typography>
                                        </Box>
                                        <Box sx={{ position: 'relative', height: 6, borderRadius: 999, bgcolor: '#eef2f6', overflow: 'hidden' }}>
                                          <Box sx={{ position: 'absolute', inset: 0, width: `${pct}%`, bgcolor: c, opacity: 0.85 }} />
                                        </Box>
                                      </Box>
                                    );
                                  })}
                                </Stack>
                              </Paper>
                            </Grid>
                          );
                        })()}

                        {/* ─── Schema drift indicator (real data only) ───────── */}
                        {(() => {
                            const ed = selectedRun.executionData || {};
                            const driftPayload = ed.schema_drift || null;
                            const sites = Array.isArray(driftPayload?.sites) ? driftPayload.sites : null;
                            if (!sites || sites.length === 0) return null;

                            const totalFields = driftPayload?.total_fields || null;
                            const aggregate = sites.reduce(
                              (acc, s) => ({
                                missing: acc.missing + (s.missing || 0),
                                extra: acc.extra + (s.extra || 0),
                                casted: acc.casted + (s.casted || 0),
                              }),
                              { missing: 0, extra: 0, casted: 0 }
                            );
                            const cleanSites = sites.filter((s) => (s.missing || 0) === 0 && (s.extra || 0) === 0).length;
                            const driftPct = sites.length > 0 ? Math.round(((sites.length - cleanSites) / sites.length) * 100) : 0;
                            const severity = driftPct === 0 ? 'pass' : driftPct < 35 ? 'warn' : 'fail';
                            const severityColor = severity === 'pass' ? '#16a34a' : severity === 'warn' ? '#d97706' : '#dc2626';
                            const severityBg = severity === 'pass' ? 'rgba(22,163,74,0.10)' : severity === 'warn' ? 'rgba(217,119,6,0.10)' : 'rgba(220,38,38,0.10)';
                            const severityLabel = severity === 'pass' ? 'Aligned' : severity === 'warn' ? 'Minor drift' : 'Drift detected';

                            return (
                              <Grid item xs={12} md={6}>
                              <Paper sx={{ p: 2, height: '100%' }} variant="outlined">
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ display: 'flex', color: severityColor }}>
                                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M3 6h18M6 12h12M9 18h6" />
                                      </svg>
                                    </Box>
                                    <Typography variant="h6">Schema drift</Typography>
                                  </Box>
                                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: 999, bgcolor: severityBg, color: severityColor, fontWeight: 700, fontSize: '0.65rem', letterSpacing: '0.05em' }}>
                                    {severityLabel.toUpperCase()}
                                  </Box>
                                </Box>
                                <Divider sx={{ mb: 1.5 }} />

                                <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                                  {[
                                    { label: 'Aligned sites', value: `${cleanSites}/${sites.length}`, color: '#16a34a' },
                                    { label: 'Missing fields', value: aggregate.missing, color: '#dc2626' },
                                    { label: 'Extra fields',   value: aggregate.extra,   color: '#d97706' },
                                    { label: 'Coerced types',  value: aggregate.casted,  color: '#7c5cbf' },
                                  ].map((m) => (
                                    <Box key={m.label} sx={{ flex: 1, border: '1px solid #eef2f6', borderRadius: 1, p: 1 }}>
                                      <Typography sx={{ color: colors.grey[600], fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                        {m.label}
                                      </Typography>
                                      <Typography sx={{ color: m.color, fontWeight: 700, fontSize: '1rem', fontFamily: 'var(--fx-font-mono)', fontVariantNumeric: 'tabular-nums', mt: 0.25 }}>
                                        {m.value}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>

                                {/* Per-site drift heat strip */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                                  {sites.map((s) => {
                                    const drift = (s.missing || 0) + (s.extra || 0);
                                    const dColor = drift === 0 ? '#16a34a' : drift <= 1 ? '#d97706' : '#dc2626';
                                    // Heat cells: total fields slot, missing red, extra amber, casted purple
                                    const slots = 12;
                                    const missingSlots = Math.min(slots, s.missing || 0);
                                    const extraSlots = Math.min(slots - missingSlots, s.extra || 0);
                                    const castedSlots = Math.min(slots - missingSlots - extraSlots, s.casted || 0);
                                    const cleanSlots = slots - missingSlots - extraSlots - castedSlots;
                                    return (
                                      <Box key={s.name} sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto 96px', alignItems: 'center', gap: 1 }}>
                                        <Typography sx={{ color: colors.grey[100] || '#0f172a', fontSize: '0.72rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {s.name}
                                        </Typography>
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, color: dColor, fontFamily: 'var(--fx-font-mono)', fontVariantNumeric: 'tabular-nums', fontWeight: 700, fontSize: '0.7rem' }}>
                                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: dColor }} />
                                          {drift === 0 ? 'ok' : `${drift} drift`}
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: '2px' }}>
                                          {Array.from({ length: missingSlots }).map((_, i) => (
                                            <Box key={`m${i}`} sx={{ flex: 1, height: 8, borderRadius: '2px', bgcolor: '#dc2626', opacity: 0.85 }} />
                                          ))}
                                          {Array.from({ length: extraSlots }).map((_, i) => (
                                            <Box key={`e${i}`} sx={{ flex: 1, height: 8, borderRadius: '2px', bgcolor: '#d97706', opacity: 0.85 }} />
                                          ))}
                                          {Array.from({ length: castedSlots }).map((_, i) => (
                                            <Box key={`c${i}`} sx={{ flex: 1, height: 8, borderRadius: '2px', bgcolor: '#7c5cbf', opacity: 0.6 }} />
                                          ))}
                                          {Array.from({ length: cleanSlots }).map((_, i) => (
                                            <Box key={`o${i}`} sx={{ flex: 1, height: 8, borderRadius: '2px', bgcolor: '#16a34a', opacity: 0.45 }} />
                                          ))}
                                        </Box>
                                      </Box>
                                    );
                                  })}
                                </Box>

                                <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'center' }}>
                                  {[
                                    { c: '#16a34a', l: 'Aligned' },
                                    { c: '#dc2626', l: 'Missing' },
                                    { c: '#d97706', l: 'Extra' },
                                    { c: '#7c5cbf', l: 'Coerced' },
                                  ].map((it) => (
                                    <Box key={it.l} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                      <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: it.c }} />
                                      <Typography sx={{ color: colors.grey[600], fontSize: '0.65rem', fontWeight: 600 }}>{it.l}</Typography>
                                    </Box>
                                  ))}
                                  <Box sx={{ flex: 1 }} />
                                  {totalFields ? (
                                    <Typography sx={{ color: colors.grey[600], fontSize: '0.65rem', fontStyle: 'italic' }}>
                                      {totalFields} fields evaluated
                                    </Typography>
                                  ) : null}
                                </Box>
                              </Paper>
                              </Grid>
                            );
                          })()}

                        <Grid item xs={12}>
                             <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<PlayArrowIcon />}
                                    onClick={() => handleUseConfiguration(selectedRun)}
                                >
                                    Use This Configuration
                                </Button>
                             </Box>
                        </Grid>
                    </Grid>
                )}

                {activeTab === 1 && (
                     <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                         {/* dev-0.7.46 — prefer the Tier1 result card iframe
                             when available; fall back to legacy loss/accuracy
                             charts when the run has no flower_task_id (pre-
                             Tier1 historic row) or the card fetch failed
                             (404, network, etc.). Loading spinner shown
                             during the in-flight authenticatedFetch. */}
                         {cardState === 'loading' ? (
                             <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320, gap: 1.5 }}>
                                 <CircularProgress size={28} thickness={4} />
                                 <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                                     Loading result card…
                                 </Typography>
                             </Box>
                         ) : cardState === 'ok' && cardBlobUrl ? (
                             <Box>
                                 <Typography variant="h6" gutterBottom>Tier1 Result Card</Typography>
                                 <Paper variant="outlined" sx={{ p: 0, borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
                                     {/* Download icon — overlays the iframe's top-right corner.
                                         Anchor uses the same blob URL the iframe renders, so no
                                         second fetch is needed. Filename mirrors
                                         PipelineResultCardViewer's convention. */}
                                     <MuiTooltip title="Download HTML">
                                         <IconButton
                                             component="a"
                                             href={cardBlobUrl}
                                             download={`federatex_${(cardTaskId || 'tier1').slice(0, 8)}_Tier1_result_card.html`}
                                             size="small"
                                             sx={{
                                                 position: 'absolute',
                                                 top: 8,
                                                 // ~1cm inset from the iframe's outer right edge
                                                 // (was right: 8). On Windows the iframe content
                                                 // can sit behind a ~17px scrollbar; the larger
                                                 // inset keeps the icon visually aligned with
                                                 // the rest of the card chrome cross-platform.
                                                 right: 40,
                                                 zIndex: 2,
                                                 bgcolor: 'rgba(255,255,255,0.92)',
                                                 color: '#475569',
                                                 boxShadow: '0 1px 3px rgba(15,23,42,0.18)',
                                                 '&:hover': {
                                                     bgcolor: '#ffffff',
                                                     color: '#0f172a',
                                                 },
                                             }}
                                         >
                                             <DownloadIcon fontSize="small" />
                                         </IconButton>
                                     </MuiTooltip>
                                     <iframe
                                         src={cardBlobUrl}
                                         title={`Result card — ${selectedRun.run_name || selectedRun.id || ''}`}
                                         sandbox="allow-same-origin"
                                         style={{ width: '100%', minHeight: 900, border: 'none', display: 'block' }}
                                     />
                                 </Paper>
                             </Box>
                         ) : selectedRun.executionData?.trainingResults ? (
                            <>
                                <Box>
                                  <Typography variant="h6" gutterBottom>Training progress over rounds</Typography>
                                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                    <Box sx={{ height: '340px', width: '100%' }}>
                                      <Line
                                        data={{
                                          labels: Object.keys(selectedRun.executionData.trainingResults).map(r => `Round ${r}`),
                                          datasets: [
                                            {
                                              label: 'Accuracy',
                                              data: Object.values(selectedRun.executionData.trainingResults).map(r => r.accuracy),
                                              borderColor: '#0087B4',
                                              backgroundColor: 'rgba(0, 135, 180, 0.10)',
                                              borderWidth: 2,
                                              pointRadius: 2,
                                              pointHoverRadius: 5,
                                              pointBackgroundColor: '#0087B4',
                                              tension: 0.35,
                                              fill: true,
                                            },
                                            {
                                              label: 'Loss',
                                              data: Object.values(selectedRun.executionData.trainingResults).map(r => r.loss),
                                              borderColor: '#7c5cbf',
                                              backgroundColor: 'rgba(124, 92, 191, 0.10)',
                                              borderWidth: 2,
                                              pointRadius: 2,
                                              pointHoverRadius: 5,
                                              pointBackgroundColor: '#7c5cbf',
                                              tension: 0.35,
                                              fill: true,
                                            }
                                          ]
                                        }}
                                        options={{
                                          responsive: true,
                                          maintainAspectRatio: false,
                                          interaction: { mode: 'index', intersect: false },
                                          plugins: {
                                            legend: {
                                              position: 'bottom',
                                              labels: { usePointStyle: true, pointStyle: 'circle', boxWidth: 8, boxHeight: 8, padding: 16, color: '#64748b', font: { size: 11, weight: '600' } }
                                            },
                                            title: { display: false },
                                            tooltip: {
                                              backgroundColor: '#0f172a', titleColor: '#e2e8f0', bodyColor: '#e2e8f0',
                                              borderColor: 'rgba(255,255,255,0.08)', borderWidth: 1, padding: 10, cornerRadius: 8,
                                              boxPadding: 4, usePointStyle: true,
                                            }
                                          },
                                          scales: {
                                            x: {
                                              grid: { display: false },
                                              ticks: { color: '#94a3b8', font: { size: 11 } },
                                              border: { color: '#e2e8f0' }
                                            },
                                            y: {
                                              beginAtZero: true,
                                              grid: { color: 'rgba(15,23,42,0.06)' },
                                              ticks: { color: '#94a3b8', font: { size: 11 }, maxTicksLimit: 6 },
                                              border: { display: false }
                                            }
                                          }
                                        }}
                                      />
                                    </Box>
                                  </Paper>
                                </Box>

                                <Box>
                                    <Typography variant="h6" gutterBottom>Round History</Typography>
                                    <Paper variant="outlined">
                                        <List disablePadding>
                                            {Object.entries(selectedRun.executionData.trainingResults)
                                                .sort(([a], [b]) => Number(b) - Number(a))
                                                .map(([round, metrics], index, arr) => (
                                                    <React.Fragment key={round}>
                                                        <ListItem>
                                                            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <Typography fontWeight="600" color={colors.primary[500]}>Round {round}</Typography>
                                                                <Box sx={{ display: 'flex', gap: 4 }}>
                                                                    <Box>
                                                                        <Typography variant="caption" color={colors.grey[500]}>Accuracy</Typography>
                                                                        <Typography fontWeight="700" sx={{ color: '#0087B4', fontFamily: 'var(--fx-font-mono)', fontVariantNumeric: 'tabular-nums' }}>
                                                                            {(metrics.accuracy * 100).toFixed(2)}%
                                                                        </Typography>
                                                                    </Box>
                                                                    <Box>
                                                                        <Typography variant="caption" color={colors.grey[500]}>Loss</Typography>
                                                                        <Typography fontWeight="700" sx={{ color: '#7c5cbf', fontFamily: 'var(--fx-font-mono)', fontVariantNumeric: 'tabular-nums' }}>
                                                                            {metrics.loss?.toFixed(4)}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        </ListItem>
                                                        {index < arr.length - 1 && <Divider />}
                                                    </React.Fragment>
                                                ))
                                            }
                                        </List>
                                    </Paper>
                                </Box>
                            </>
                         ) : (
                             <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
                                 <Typography color="textSecondary">No detailed execution graph data available.</Typography>
                             </Box>
                         )}
                     </Box>
                )}

                {activeTab === 2 && (
                    <Box sx={{ height: '100%', overflow: 'hidden', backgroundColor: '#020617', borderRadius: '20px' }}>
                         {isLoadingLogs ? (
                           <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                             <Typography color={colors.grey[500]}>Loading logs...</Typography>
                           </Box>
                         ) : (
                           <ExecutionLogsCard 
                               logs={executionLogs} 
                               colors={colors} 
                               defaultExpanded={true}
                               defaultShowDetails={true}
                           />
                         )}
                    </Box>
                )}
             </Box>
          </>
        )}
      </DialogContent>
      
      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default AllCompletedRunsModal;
